/**
 * Application state.
 *
 * Hand-rolled rather than pulled from a library: this app has one store, six screens and no
 * server-driven routing, and a subscription plus `useSyncExternalStore` covers all of it in fifty
 * lines. The interesting part is not the store, it is that every mutation is optimistic, queued,
 * and replayed — see `act`.
 */

import { useEffect, useState, useSyncExternalStore } from 'react';

import { content as bundledContent } from '../core/load';
import { contentSchema, resolveContent, type RawContent, type ResolvedContent } from '../core/content';
import { emptyPosada, type PosadaState } from '../core/posada';
import {
  emptyTally,
  urgentDuel,
  type BlindLabel,
  type Duel,
  type Osa,
  type Tally,
} from '../core/dvoboj';
import { readDom, type DomDoor, type RawDom } from '../core/dom';
import { readMesto, sameMesto, type Mesto } from '../core/mesto';
import type { NodeProgress, PlayerState, TimeBudget } from '../core/rules';
import { copy } from '../core/copy';
import {
  ApiError,
  api,
  localQueue,
  localState,
  newClientId,
  type Bootstrap,
  type DuelView,
  type DomView,
  type PosadaView,
  type QueuedAction,
  type ServerState,
} from './api';
import { sfx } from './audio';

export type Screen = 'sada' | 'mapa' | 'ostrvo' | 'dnevnik' | 'dom' | 'posada' | 'dvoboj' | 'admin';
export type Phase = 'loading' | 'login' | 'confirm' | 'onboarding' | 'game' | 'blocked';

export interface Toast {
  id: number;
  title: string;
  body?: string;
  tone: 'normal' | 'gold' | 'green';
}

export interface AppState {
  phase: Phase;
  bootstrap: Bootstrap | null;
  server: ServerState | null;
  /** The runtime curriculum from the server. Null until it arrives; the bundled file answers. */
  content: RawContent | null;
  /**
   * True when the server reports a content version this client failed to fetch or parse.
   * The only way out is a reload, which picks up a bundle that understands the new content.
   */
  contentStale: boolean;
  screen: Screen;
  budget: TimeBudget;
  /**
   * How far down the free island's ranking „DRUGA VEŽBA" has walked.
   *
   * A view parameter exactly like `budget`, and deliberately client-only and unpersisted: it is not
   * a record of exercises the member has refused, it is where they are standing in a list this
   * session. It resets when the time budget changes and after any submitted work, so the offer is
   * stable while they are deciding and fresh once they have done something.
   */
  offerSkip: number;
  openIsland: string | null;
  openNode: string | null;
  /** The crew member whose posts to show first, set by walking into them or by „vrati komentar". */
  focusPeer: string | null;
  /** Who is on the map and whose work is waiting. Fetched, not pushed — see loadPosadaView. */
  posadaView: PosadaView | null;
  /** The duel the arena is showing. Null whenever the battle screen is not open. */
  openDuel: string | null;
  duelView: DuelView | null;
  /** MOJE OSTRVO: the island on screen, mine or a host's. Null until the screen is opened. */
  dom: DomScreen | null;
  domSave: DomSave;
  domBusy: boolean;
  toasts: Toast[];
  online: boolean;
  pending: number;
  busy: boolean;
  fatal: string | null;
  /** Both only ever set in development, so login works without a mailbox. */
  devLink: string | null;
  devCode: string | null;
  loginStatus: 'idle' | 'sending' | 'sent' | 'no-mail' | 'not-member' | 'rate-limited' | 'bad-email';
  confirmStatus: 'idle' | 'working' | 'expired' | 'used' | 'invalid';
  codeStatus: 'idle' | 'working' | 'invalid' | 'expired' | 'locked' | 'rate-limited';
  seenFeatures: string[];
  /** Members this device has chosen not to see. Local, silent, and never sent anywhere. */
  hidden: string[];
}

let state: AppState = {
  phase: 'loading',
  bootstrap: null,
  server: null,
  content: readCachedContent(),
  contentStale: false,
  screen: 'sada',
  budget: null,
  offerSkip: 0,
  openIsland: null,
  openNode: null,
  focusPeer: null,
  posadaView: null,
  openDuel: null,
  duelView: null,
  dom: null,
  domSave: 'idle',
  domBusy: false,
  toasts: [],
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  pending: 0,
  busy: false,
  fatal: null,
  devLink: null,
  devCode: null,
  loginStatus: 'idle',
  confirmStatus: 'idle',
  codeStatus: 'idle',
  seenFeatures: readSeenFeatures(),
  hidden: readHidden(),
};

const listeners = new Set<() => void>();
let toastId = 1;

function set(patch: Partial<AppState>): void {
  state = { ...state, ...patch };
  for (const listener of listeners) listener();
}

export function useStore<T>(select: (s: AppState) => T): T {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => select(state),
    () => select(state),
  );
}

export const getState = (): AppState => state;

/* ------------------------------------------------------------------ runtime content */

const CONTENT_KEY = 'talas.content.v1';

function readCachedContent(): RawContent | null {
  try {
    const raw = localStorage.getItem(CONTENT_KEY);
    if (!raw) return null;
    // Schema-parsed, not trusted: a cache written by a newer bundle, or a hand-edited one, must
    // degrade to the bundled fallback rather than crash the rules engine.
    const parsed = contentSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

function persistContent(data: RawContent): void {
  try {
    localStorage.setItem(CONTENT_KEY, JSON.stringify(data));
  } catch {
    /* private mode */
  }
}

/** The raw curriculum this client is playing against: runtime if present, else the bundled seed. */
export function rawContentOf(s: AppState = state): RawContent {
  return s.content ?? bundledContent();
}

export function heldContentVersion(s: AppState = state): number {
  return rawContentOf(s).verzija ?? 1;
}

export interface PathInfo {
  id: string;
  name: string;
  description: string;
  active: boolean;
  note?: string;
}

let pathsMemo: { raw: RawContent; value: PathInfo[] } | null = null;

export function availablePathsOf(s: AppState = state): PathInfo[] {
  const raw = rawContentOf(s);
  if (pathsMemo?.raw === raw) return pathsMemo.value;
  const value = Object.entries(raw.putanje).map(([id, p]) => ({
    id,
    name: p.naziv,
    description: p.opis,
    active: p.aktivna !== false,
    note: p.napomena,
  }));
  pathsMemo = { raw, value };
  return value;
}

export function defaultPathOf(s: AppState = state): string {
  return availablePathsOf(s).find((p) => p.active)?.id ?? 'adobe';
}

/** Same coercion the server applies: a path that stopped existing falls back to the default. */
function usablePathOf(s: AppState = state): string {
  const path = s.server?.member.path;
  if (path && availablePathsOf(s).some((p) => p.id === path && p.active)) return path;
  return defaultPathOf(s);
}

let resolveMemo: { raw: RawContent; byPath: Map<string, ResolvedContent> } | null = null;

function resolveFor(raw: RawContent, path: string): ResolvedContent {
  if (resolveMemo?.raw !== raw) resolveMemo = { raw, byPath: new Map() };
  const hit = resolveMemo.byPath.get(path);
  if (hit) return hit;
  const value = resolveContent(raw, path);
  resolveMemo.byPath.set(path, value);
  return value;
}

function applyContent(candidate: unknown): boolean {
  const parsed = contentSchema.safeParse(candidate);
  if (!parsed.success) return false;
  persistContent(parsed.data);
  set({ content: parsed.data, contentStale: false });
  return true;
}

let refreshingContent = false;

/**
 * The server just answered with a content version this client does not hold — an admin published
 * while this tab was open. Content is data, so the fix is a fetch, not a page reload; the reload
 * banner is only for the case where the fetch or the parse fails (this bundle is too old to
 * understand the new content).
 */
async function maybeRefreshContent(serverVersion: number): Promise<void> {
  // Only ever forwards. A response from an isolate whose cache has not caught up yet reports an
  // older version; following it would flap the whole app back and forth for a few seconds.
  if (serverVersion <= heldContentVersion() || refreshingContent) return;
  refreshingContent = true;
  try {
    const result = await api.content();
    if (result.version < heldContentVersion()) return;
    if (!applyContent(result.content)) set({ contentStale: true });
  } catch {
    // Offline is not staleness — the queue flush will land eventually and ask again.
    if (state.online) set({ contentStale: true });
  } finally {
    refreshingContent = false;
  }
}

/** Pull the active curriculum right now — used by the editor straight after a publish. */
export async function refreshContentNow(): Promise<void> {
  try {
    const result = await api.content();
    if (result.version < heldContentVersion()) return;
    if (!applyContent(result.content)) set({ contentStale: true });
  } catch {
    /* the next state response will ask again */
  }
}

/* ------------------------------------------------------------------ derived */

export function contentOf(s: AppState = state): ResolvedContent {
  return resolveFor(rawContentOf(s), usablePathOf(s));
}

/**
 * Derived, and cached against the server state it was derived from.
 *
 * `useSyncExternalStore` compares snapshots by identity, so a selector that builds a fresh object
 * on every call re-renders forever. Keying the cache on the `server` object reference is exact:
 * that object is replaced precisely when progress changes and never otherwise.
 */
let playerCache: {
  key: ServerState | null;
  contentKey: RawContent | null;
  value: PlayerState;
} | null = null;

export function playerOf(s: AppState = state): PlayerState {
  const key = s.server;
  const contentKey = s.content;
  if (playerCache && playerCache.key === key && playerCache.contentKey === contentKey)
    return playerCache.value;
  const value: PlayerState = {
    day: key?.member.day ?? 0,
    path: usablePathOf(s),
    progress: key?.progress ?? {},
    openedIslands: key?.openedIslands ?? [],
    clearedIslands: key?.clearedIslands ?? [],
  };
  playerCache = { key, contentKey, value };
  return value;
}

export const isReadOnly = (s: AppState = state): boolean => s.server?.member.status === 'revoked';

/**
 * The crew half of the server state.
 *
 * Defensive on purpose: a state cached by a bundle from before this feature shipped has no
 * `posada` key at all, and it is read on the first paint of the very first load after a deploy.
 * A missing crew is an empty crew, not a crash. Cached against the state object it came from, so
 * the empty case does not hand `useSyncExternalStore` a new object on every call.
 */
let emptyPosadaValue: PosadaState | null = null;

export function posadaOf(s: AppState = state): PosadaState {
  const held = s.server?.posada;
  if (held) return held;
  if (!emptyPosadaValue) emptyPosadaValue = emptyPosada();
  return emptyPosadaValue;
}

/**
 * The duels this member is in, as the shell needs them.
 *
 * Defensive in exactly the same way as `posadaOf`, and for the same reason: a state cached by a
 * bundle from before this feature has no `dvoboji` key, and it is read on the first paint after a
 * deploy.
 *
 * Challenges from somebody this device has hidden are dropped here. „Sakrij" is held on the device
 * and never sent (`docs/UZIVO.md` §2), so this is the only place it can be applied — and it has to
 * be, or the one protection in the product would have a hole in it exactly where it matters most.
 */
const NO_DUELS: Duel[] = [];
let duelsCache: { key: ServerState | null; hidden: string[]; value: Duel[] } | null = null;

export function duelsOf(s: AppState = state): Duel[] {
  const held = s.server?.dvoboji;
  if (!held || held.length === 0) return NO_DUELS;
  if (duelsCache && duelsCache.key === s.server && duelsCache.hidden === s.hidden)
    return duelsCache.value;
  const value = held.filter((duel) => {
    const them = duel.fromId === s.server?.member.id ? duel.toId : duel.fromId;
    return !s.hidden.includes(them);
  });
  duelsCache = { key: s.server ?? null, hidden: s.hidden, value };
  return value;
}

export const tallyOf = (s: AppState, duelId: string): Tally =>
  s.server?.dvobojGlasovi?.[duelId] ?? emptyTally();

/** The one duel worth handing the SADA card to, or null. */
let urgentCache: { key: ServerState | null; hidden: string[]; value: Duel | null } | null = null;

export function urgentDuelOf(s: AppState = state): Duel | null {
  const id = s.server?.member.id;
  if (!id) return null;
  // Cached against the state it was derived from, like `playerOf`: this IS a store selector, so it
  // has to return the same object until the state behind it actually changes. `serverNow` moving a
  // second is not a change of which duel is urgent.
  if (urgentCache && urgentCache.key === s.server && urgentCache.hidden === s.hidden)
    return urgentCache.value;
  const value = urgentDuel(duelsOf(s), id, serverNow());
  urgentCache = { key: s.server ?? null, hidden: s.hidden, value };
  return value;
}

/**
 * The clock the duel runs on is the server's, not the device's.
 *
 * A phone whose clock is twenty minutes fast would otherwise show a deadline that has passed and
 * refuse a submission the server would have taken. The offset is measured once per state response
 * and applied everywhere a duel asks what time it is.
 *
 * **Never pass this to `useStore`.** It returns a different number on every call, and
 * `useSyncExternalStore` compares snapshots by identity — a selector that is never equal to itself
 * re-renders forever, which is a frozen tab rather than a slow one. Use `useNow` below, which ticks
 * on a timer the way a clock actually should.
 */
export function serverNow(): number {
  if (!serverTimeSeen) return Date.now();
  return Date.now() + (serverTimeSeen - serverReceivedAt);
}

let serverReceivedAt = Date.now();
let serverTimeSeen = 0;

/**
 * The server's clock, re-read on a timer.
 *
 * A deadline is the one thing in this product that changes without anybody doing anything, so the
 * screens that show one need a tick. One a second: it is what a countdown looks like, and the
 * canvas animates from its own `elapsed` and asks nothing of React.
 */
export function useNow(everyMs = 1000): number {
  const [, tick] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => tick((n) => n + 1), everyMs);
    return () => window.clearInterval(timer);
  }, [everyMs]);
  return serverNow();
}

/* ------------------------------------------------------------------ toasts */

export function toast(title: string, body?: string, tone: Toast['tone'] = 'normal'): void {
  const entry: Toast = { id: toastId++, title, body, tone };
  set({ toasts: [...state.toasts.slice(-2), entry] });
  if (tone === 'gold') sfx.fanfare();
  else sfx.confirm();
  setTimeout(() => set({ toasts: state.toasts.filter((t) => t.id !== entry.id) }), 4200);
}

export function dismissToast(id: number): void {
  set({ toasts: state.toasts.filter((t) => t.id !== id) });
}

/* ------------------------------------------------------------------ boot */

let booting = false;

export async function boot(): Promise<void> {
  if (booting) return;
  booting = true;
  // Show the cached state immediately so a returning student never stares at a spinner.
  const cached = localState.read();
  if (cached) {
    adoptMesto(cached);
    set({ server: cached, phase: cached.member.path ? 'game' : 'onboarding' });
  }

  try {
    const bootstrap = await api.bootstrap();
    // Content first, so the state that follows is judged against the curriculum it came with.
    if (bootstrap.content) applyContent(bootstrap.content);
    if (bootstrap.state) {
      applyServerState(bootstrap.state);
      set({
        bootstrap,
        phase: bootstrap.state.member.path ? 'game' : 'onboarding',
      });
      void flushQueue();
    } else {
      localState.forgetSession();
      set({ bootstrap, server: null, phase: 'login' });
    }
  } catch (error) {
    if (cached) {
      // Offline with a cached session: let them keep playing, queue everything.
      set({ online: false, phase: cached.member.path ? 'game' : 'onboarding' });
      return;
    }
    set({ phase: 'login', fatal: error instanceof ApiError ? null : copy.error.genericBody });
  } finally {
    booting = false;
  }
}

function applyServerState(next: ServerState): void {
  // Stamped before anything reads it, so `serverNow` measures the round trip against the response
  // it actually came from.
  serverReceivedAt = Date.now();
  serverTimeSeen = next.serverTime ?? 0;
  /**
   * Where the last device left off — adopted only into an empty slot.
   *
   * `applyServerState` runs on every poll and after every write, so an unconditional assignment here
   * would drag a walking member back to whatever tile the server last heard about, twenty seconds
   * ago, forever. Seeding only a null is what makes "the phone continues where the desktop stopped"
   * true without letting the server steer anybody who is already moving.
   */
  adoptMesto(next);
  localState.write(next);
  set({ server: next, pending: localQueue.all().length });
  // An admin may have published while this tab was open. Data, not code — fetch, don't reload.
  void maybeRefreshContent(next.contentVersion);
}

/* ------------------------------------------------------------------ auth */

export async function requestLogin(email: string): Promise<void> {
  set({ loginStatus: 'sending', devLink: null, devCode: null, codeStatus: 'idle' });
  try {
    const result = await api.requestLogin(email);
    if (result.status === 'sent')
      set({ loginStatus: 'sent', devLink: result.devLink ?? null, devCode: result.devCode ?? null });
    else if (result.status === 'no-mail')
      set({ loginStatus: 'no-mail', devLink: result.devLink ?? null, devCode: result.devCode ?? null });
    else if (result.status === 'not-member') set({ loginStatus: 'not-member' });
    else if (result.status === 'rate-limited') set({ loginStatus: 'rate-limited' });
    else set({ loginStatus: 'bad-email' });
  } catch (error) {
    if (error instanceof ApiError && error.status === 429) set({ loginStatus: 'rate-limited' });
    else if (error instanceof ApiError && error.status === 400) set({ loginStatus: 'bad-email' });
    else set({ loginStatus: 'idle', fatal: copy.error.genericBody });
  }
}

/** The landing that both ways in share, once the server has said yes. */
function enterApp(result: Partial<ServerState>): void {
  const next = result as unknown as ServerState;
  applyServerState(next);
  history.replaceState(null, '', '/');
  // replaceState does not fire popstate, and App keeps the route in React state — without this
  // the confirm screen stays up after a successful login and the next click burns the token.
  window.dispatchEvent(new PopStateEvent('popstate'));
  set({
    phase: next.member.path ? 'game' : 'onboarding',
    confirmStatus: 'idle',
    codeStatus: 'idle',
    screen: 'sada',
  });
  void flushQueue();
}

export async function confirmLogin(token: string): Promise<void> {
  set({ confirmStatus: 'working' });
  try {
    const result = await api.confirmLogin(token);
    if (result.status !== 'ok' || !result.member) {
      set({
        confirmStatus:
          result.status === 'expired' ? 'expired' : result.status === 'used' ? 'used' : 'invalid',
      });
      return;
    }
    enterApp(result);
  } catch (error) {
    const code = error instanceof ApiError ? String((error.detail as { status?: string })?.status) : '';
    set({ confirmStatus: code === 'expired' ? 'expired' : code === 'used' ? 'used' : 'invalid' });
  }
}

/**
 * The typed-code way in. The address goes up with the code because the mail may well have been
 * opened on a different device, so this browser has nothing else to identify the request by.
 */
export async function submitCode(email: string, code: string): Promise<void> {
  set({ codeStatus: 'working' });
  try {
    const result = await api.confirmCode(email, code);
    if (result.status !== 'ok' || !result.member) {
      set({ codeStatus: codeFailure(result.status) });
      return;
    }
    enterApp(result);
  } catch (error) {
    if (error instanceof ApiError && error.status === 429) {
      set({ codeStatus: 'rate-limited' });
      return;
    }
    const status = error instanceof ApiError ? String((error.detail as { status?: string })?.status) : '';
    set({ codeStatus: codeFailure(status) });
  }
}

function codeFailure(status: string): 'invalid' | 'expired' | 'locked' | 'rate-limited' {
  if (status === 'expired') return 'expired';
  if (status === 'locked') return 'locked';
  if (status === 'rate-limited') return 'rate-limited';
  return 'invalid';
}

export async function logout(): Promise<void> {
  try {
    await api.logout();
  } catch {
    // Signing out locally matters more than the round trip succeeding.
  }
  // The cached state goes; the unsent queue does NOT. Someone who signs out on a train with work
  // still in the queue gets it delivered when they sign back in.
  localState.forgetSession();
  // A remembered position is not queued work, and the next person to sign in on this device must
  // not be dropped onto somebody else's island.
  forgetMesto();
  set({ server: null, phase: 'login', screen: 'sada', loginStatus: 'idle', budget: null });
}

/**
 * Which island the map has this member standing on, and therefore which room they are in.
 *
 * Module state rather than a field on the store, because nothing renders off it: it changes six
 * times a second while somebody walks, and a store update per step would re-render every screen
 * that subscribes. `Mapa` sets it where it joins a room and clears it on the way out; the only
 * reader is the appearance save, which needs it to tell the right room.
 */
let liveIsland: string | null = null;

export function setLiveIsland(islandId: string | null): void {
  liveIsland = islandId;
}

/* ------------------------------------------------------------------ where they were standing */

/**
 * The last tile this member stood on, as an offset from its island's centre.
 *
 * Module state for exactly the same reason `liveIsland` is: it changes about six times a second
 * while somebody walks, and putting it in the store would re-render every subscribed screen once per
 * step. Nothing renders from it — the only reader is the map, once, when it builds an engine.
 *
 * **The safety property of this whole mechanism is that a server response may never move the
 * player.** `mesto` is seeded from `/api/state` only while it is still null (a fresh tab), so a poll
 * landing while somebody is mid-stride cannot teleport them, and two tabs are last-writer-wins on
 * the row without either one being dragged around by the other. If that ever stops being true, the
 * bug looks like the character twitching backwards every twenty seconds.
 */
let mesto: Mesto | null = null;
/** What the server was last told, so an unchanged position is never sent twice. */
let mestoSent: Mesto | null = null;
/**
 * Whose position this is.
 *
 * Module state outlives a sign-out that did not go through `logout()` — a session that expired, a
 * cookie cleared, a second account on a shared laptop — so without this the next member to sign in
 * on that device is restored to the previous one's island and then writes it to their own row.
 */
let mestoOwner: string | null = null;
let mestoTimer = 0;

/**
 * How often a position may reach the server.
 *
 * A walk is six tiles a second and the value is worth nothing until somebody leaves, so this is
 * slow on purpose. The forced flushes (`pagehide`, leaving the map) are what actually make it
 * accurate; the interval is only there so a browser that is killed without either one still has
 * something recent.
 */
const MESTO_FLUSH_MS = 10_000;

export const getMesto = (): Mesto | null => mesto;

/**
 * Take the stored position out of a state response, into an empty slot.
 *
 * Called from every path that installs a session — including the cached one `boot` shows before the
 * network answers, which used to be missed entirely: an offline or slow start restored the wrong
 * island and then overwrote the stored one with wherever the fallback had put them.
 */
function adoptMesto(next: ServerState): void {
  // A different member on the same device inherits nothing.
  if (mestoOwner && mestoOwner !== next.member.id) forgetMesto();
  mestoOwner = next.member.id;
  if (mesto) return;
  const remembered = readMesto(next.mesto);
  if (!remembered) return;
  mesto = remembered;
  mestoSent = remembered;
}

export function setMesto(next: Mesto): void {
  if (sameMesto(mesto, next)) return;
  mesto = next;
  if (mestoTimer) return;
  mestoTimer = window.setTimeout(() => {
    mestoTimer = 0;
    flushMesto();
  }, MESTO_FLUSH_MS);
}

/**
 * Send it, if there is anything to send.
 *
 * Fire and forget, and deliberately **not** in `localQueue`. Everything in that queue is a fact
 * about work somebody did, replayed until the server admits it. A position is not a fact — it is
 * where somebody happened to be — and one replayed out of a queue an hour later would move a member
 * backwards to a tile they had already left. The crew and duel writes make the same argument.
 */
export function flushMesto(): void {
  if (!mesto || sameMesto(mesto, mestoSent) || !state.server) return;
  if (isReadOnly()) return;
  const sending = mesto;
  mestoSent = sending;
  void api.saveMesto(sending).catch(() => {
    // Let the next flush try again. Nothing on screen depends on this having landed.
    if (sameMesto(mestoSent, sending)) mestoSent = null;
  });
}

/** Session over: the next member on this device must not inherit a position. */
function forgetMesto(): void {
  if (mestoTimer) window.clearTimeout(mestoTimer);
  mestoTimer = 0;
  mesto = null;
  mestoSent = null;
  mestoOwner = null;
}

/**
 * Two view preferences that used to live in `Mapa`'s component state and were therefore thrown away
 * every time somebody looked at a task.
 *
 * `wide` is the zoom, which dropped back to close-up on every screen change. `touch` is worse: the
 * d-pad and the A button only appear once the canvas has seen a `touchstart`, so a phone user who
 * opened a card came back to a map with no controls at all until they thought to tap it. Neither is
 * worth a row in the database and neither may be store state (the map writes them mid-render), so
 * they are module-scoped alongside the position.
 */
export const mapView = {
  wide: false,
  touch: false,
};

/* ------------------------------------------------------------------ moje ostrvo */

/**
 * The island on screen — mine, or somebody else's if I have been let in.
 *
 * Held in the store rather than in the component because it survives walking off the screen and back,
 * and because the save state has to be visible from the chrome. `rev` is the whole concurrency story:
 * it goes out with every save and a mismatch comes back as `stale`, which is sticky — the same shape
 * the curriculum editor's conflict state has, and for the same reason. Continuing to paint into a
 * document the server will not keep is the worst thing an editor can do quietly.
 */
export type DomSave = 'idle' | 'dirty' | 'saving' | 'saved' | 'stale' | 'error';

export interface DomScreen extends DomView {
  world: RawDom;
}

let domTimer = 0;
/**
 * The save currently in flight, if there is one.
 *
 * A promise rather than a boolean, so a second flush can wait for the first rather than race it.
 * Without this, two PUTs go out carrying the same `rev` — a save fires on its timer, the member
 * paints once more and taps PROŠETAJ, which flushes again — the first bumps the row, the second is
 * refused as stale, and the editor tells a member on one phone that their island „je izmenjeno na
 * drugom uređaju". The rev guard is only sound if one client never has two writes outstanding.
 */
let domInFlight: Promise<void> | null = null;
/** What the server last accepted, so an island edited back to its saved state is not sent again. */
let domSaved: string | null = null;

const DOM_SAVE_MS = 900;

export async function loadDom(ownerId?: string): Promise<void> {
  set({ domBusy: true });
  // A different island is a different document; nothing about the last one's save state carries.
  domSaved = null;
  try {
    const view = await api.dom.get(ownerId);
    const world = readDom(view.svet);
    if (!world) {
      set({ domBusy: false, dom: null, domSave: 'error' });
      return;
    }
    set({ dom: { ...view, world }, domSave: 'idle', domBusy: false });
  } catch {
    set({ domBusy: false, dom: null, domSave: 'error' });
  }
}

/** Change my island locally and schedule the save. Never applied to somebody else's. */
export function editDom(next: RawDom): void {
  const current = state.dom;
  if (!current?.mine) return;
  const clean = JSON.stringify(next) === domSaved;
  set({
    dom: { ...current, world: next },
    domSave: state.domSave === 'stale' ? 'stale' : clean ? 'saved' : 'dirty',
  });
  if (state.domSave === 'stale' || clean) return;
  if (domTimer) window.clearTimeout(domTimer);
  domTimer = window.setTimeout(() => void flushDom(), DOM_SAVE_MS);
}

export async function flushDom(): Promise<void> {
  if (domTimer) window.clearTimeout(domTimer);
  domTimer = 0;
  // One write at a time. The second waits for the first and then re-reads the world, so it carries
  // the revision the first one produced rather than the one they both started from.
  if (domInFlight) {
    await domInFlight;
    if (state.domSave !== 'dirty') return;
  }

  const current = state.dom;
  if (!current?.mine || state.domSave === 'stale') return;

  const payload = JSON.stringify(current.world);
  if (payload === domSaved) {
    set({ domSave: 'saved' });
    return;
  }

  set({ domSave: 'saving' });
  const attempt = (async () => {
    try {
      const result = await api.dom.save(current.world, current.rev);
      domSaved = payload;
      const now = state.dom;
      /**
       * The dock may have moved.
       *
       * It is the one thing the server changes on the way in: a member can paint the sea over the
       * tile a guest arrives on, and relocating it beats refusing the save (which used to mean every
       * later save failed too, silently). Adopted only when nothing has been drawn since — a world
       * written back over a live brush stroke would take the stroke with it.
       */
      const kept = readDom(result.svet);
      if (kept && now?.mine && JSON.stringify(now.world) === payload && kept.dok !== undefined) {
        const moved = kept.dok.x !== current.world.dok.x || kept.dok.y !== current.world.dok.y;
        if (moved) {
          set({ dom: { ...now, world: kept } });
          domSaved = JSON.stringify(kept);
        }
      }
      /**
       * Only the revision is adopted, and only onto my own island.
       *
       * The world is never written back — a response landing mid-stroke would take back a live brush
       * stroke — and if the member has walked off to somebody else's island while this was in
       * flight, `now.mine` is false and this must not stamp my revision onto their view.
       */
      if (now?.mine) set({ dom: { ...now, rev: result.rev, vrati: true } });
      set({ domSave: JSON.stringify(state.dom?.world) === payload ? 'saved' : 'dirty' });
    } catch (error) {
      /**
       * 409 means another device saved first.
       *
       * Sticky, and nothing further is sent: silently overwriting is how an afternoon of work on a
       * laptop disappears because a phone still had the screen open. The member is offered a reload,
       * which is the only honest way out.
       */
      if (error instanceof ApiError && error.status === 409) set({ domSave: 'stale' });
      else set({ domSave: 'error' });
    }
  })();
  domInFlight = attempt;
  await attempt;
  if (domInFlight === attempt) domInFlight = null;
}

export async function nameDom(a: number, b: number): Promise<void> {
  const current = state.dom;
  if (!current?.mine) return;
  set({ dom: { ...current, ime: [a, b] } });
  try {
    await api.dom.name(a, b);
  } catch {
    /* The name is one tap to set again; it is not worth a message. */
  }
}

export async function doorDom(vrata: DomDoor): Promise<string | null> {
  const current = state.dom;
  if (!current?.mine) return null;
  try {
    const result = await api.dom.door(vrata);
    const now = state.dom;
    if (now) set({ dom: { ...now, vrata: result.vrata, kod: result.kod } });
    return null;
  } catch (error) {
    /**
     * The one place a refusal has something to say, and the sentences live here rather than on the
     * server — this file is where every string a member reads is written.
     *
     * Two of them: the island would strand whoever came to visit (the server explains which part),
     * and „you are out of the crew", which has its own sentence and used to fall through to
     * „Nije sačuvano. Probaj ponovo." — a message about a save, for something that is not one.
     */
    if (error instanceof ApiError && error.code === 'van-posade') return copy.dom.doorNeedsCrew;
    const detail = error instanceof ApiError ? (error.detail as { message?: string }) : null;
    return detail?.message ?? copy.dom.saveFailed;
  }
}

export async function revertDom(): Promise<void> {
  const current = state.dom;
  if (!current?.mine) return;
  try {
    const view = await api.dom.revert();
    const world = readDom(view.svet);
    if (world) set({ dom: { ...current, ...view, world }, domSave: 'saved' });
  } catch {
    set({ domSave: 'error' });
  }
}

export async function onboard(path: string, look: number): Promise<void> {
  set({ busy: true });
  try {
    // The island goes along for the same reason it does on a plain appearance save: settings can
    // change the path and the character in one press, and the people standing beside this member
    // have to see the second one.
    const next = await api.onboard(path, look, undefined, liveIsland);
    applyServerState(next);
    set({ phase: 'game', screen: 'sada', busy: false });
    sfx.fanfare();
  } catch {
    set({ busy: false, fatal: copy.error.genericBody });
  }
}

/**
 * A new appearance, applied here first and saved behind it.
 *
 * Optimistic and not queued, unlike progress. The difference is what a lost write costs: a posted
 * work that never reaches the server is the one thing this product exists to record, so it is
 * replayed forever; an appearance that does not save is a haircut you have to choose again. Queuing
 * it would mean carrying an appearance in the offline queue that could arrive after a later one and
 * silently undo it, which is a worse failure than the one it prevents.
 */
export async function saveIzgled(look: number): Promise<void> {
  const current = state.server;
  if (!current || current.member.look === look) return;
  const previous = current.member.look;
  set({ server: { ...current, member: { ...current.member, look } } });
  try {
    await api.saveIzgled(look, liveIsland);
  } catch {
    const now = state.server;
    // Put the face back rather than leaving somebody looking at a character the server disagrees
    // with — the next refresh would swap it out from under them with no explanation.
    if (now) set({ server: { ...now, member: { ...now.member, look: previous } } });
    toast(copy.error.generic, copy.error.genericBody);
  }
}

/* ------------------------------------------------------------------ progress */

function optimistic(progress: Record<string, NodeProgress>, action: QueuedAction): Record<string, NodeProgress> {
  const current = progress[action.nodeId] ?? { nodeId: action.nodeId };
  const next: NodeProgress = { ...current };
  if (action.action === 'done') next.doneAt = action.at;
  if (action.action === 'post') {
    next.v1Url = action.url ?? null;
    next.v1At = action.at;
  }
  if (action.action === 'feedback') next.feedbackAt = action.at;
  if (action.action === 'revise') {
    next.v2Url = action.url ?? null;
    next.v2At = action.at;
  }
  return { ...progress, [action.nodeId]: next };
}

export type ActResult = { ok: true } | { ok: false; message: string };

/**
 * The one write path.
 *
 * Applies the change locally first, then tries the server. A network failure is not an error
 * here — the action goes into a durable queue and the student carries on. Only a rejection the
 * server actually reasoned about (a duplicate link, a locked node) is surfaced and rolled back.
 */
export async function act(
  nodeId: string,
  action: QueuedAction['action'],
  url?: string,
): Promise<ActResult> {
  if (!state.server) return { ok: false, message: copy.submit.failed };
  if (isReadOnly()) return { ok: false, message: copy.login.readOnly };

  const entry: QueuedAction = { clientId: newClientId(), nodeId, action, url, at: Date.now() };
  const before = state.server;
  // Doing something ends the browse. The next offer starts at the top of the ranking again.
  if (state.offerSkip !== 0) set({ offerSkip: 0 });

  const optimisticState: ServerState = { ...before, progress: optimistic(before.progress, entry) };
  applyServerState(optimisticState);

  try {
    const next = await api.progress(entry);
    if (next.member) {
      applyServerState(next as ServerState);
      announce(before, next as ServerState, action);
    } else {
      // A no-op answer ("duplicate") carries no state, so the optimistic write is unverified.
      // Pull the truth rather than leaving the two out of step.
      applyServerState(await api.state());
    }
    return { ok: true };
  } catch (error) {
    if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
      applyServerState(before);
      return { ok: false, message: messageFor(error) };
    }
    // Network, timeout, 5xx: keep the optimistic result and try again later.
    localQueue.push(entry);
    set({ pending: localQueue.all().length, online: false });
    toast(copy.error.offline, copy.submit.savedOffline);
    return { ok: true };
  }
}

function messageFor(error: ApiError): string {
  const detail = error.detail as { error?: string; problem?: string } | undefined;
  switch (detail?.error) {
    case 'duplicate-link':
      return copy.submit.duplicate;
    case 'bad-link':
      return detail.problem === 'predugacko' ? copy.submit.tooLong : copy.submit.badUrl;
    case 'locked':
      return copy.node.lockedHint;
    case 'needs-feedback':
      return copy.node.gotFeedbackHint;
    case 'read-only':
      return copy.login.readOnly;
    default:
      return copy.submit.failed;
  }
}

/** Islands and screens that just appeared. Announced once, never as a pile of notifications. */
function announce(before: ServerState, after: ServerState, action: QueuedAction['action']): void {
  if (action === 'revise') toast(copy.toast.revised, copy.toast.revisedBody, 'gold');
  else if (action === 'post') toast(copy.toast.posted, copy.toast.postedBody);

  const content = contentOf();
  for (const id of after.clearedIslands) {
    if (before.clearedIslands.includes(id)) continue;
    const island = content.islands.find((i) => i.id === id);
    if (!island) continue;
    // The moment the island clears is the moment the souvenir goes on. Say that, not the goal —
    // the goal was the pitch, the souvenir is the proof.
    const souvenir = island.souvenir ? copy.souvenir[island.souvenir] : undefined;
    toast(copy.toast.islandCleared(island.name), souvenir?.nosis ?? island.goal ?? undefined, 'gold');
  }
  for (const id of after.openedIslands) {
    if (before.openedIslands.includes(id)) continue;
    if (after.clearedIslands.includes(id)) continue;
    const island = content.islands.find((i) => i.id === id);
    if (island && island.order > 0) toast(copy.toast.islandOpened(island.name), undefined, 'gold');
  }
}

/* ------------------------------------------------------------------ the offline queue */

let flushing = false;

/** The server processes at most this many per request, so send at most this many. */
const BATCH_LIMIT = 50;

export async function flushQueue(): Promise<void> {
  if (flushing) return;
  const queued = localQueue.all();
  if (queued.length === 0) {
    if (state.pending !== 0) set({ pending: 0 });
    return;
  }
  const items = queued.slice(0, BATCH_LIMIT);
  flushing = true;
  try {
    const result = await api.progressBatch(items);

    // Remove only what the server actually answered for. Removing the whole batch would drop
    // anything past the server's own limit on the floor — silently, and for good.
    const answered = new Set(
      (result.results ?? []).map((r) => r.clientId).filter((id): id is string => Boolean(id)),
    );
    localQueue.remove(items.map((item) => item.clientId).filter((id) => answered.has(id)));

    // An item the server reasoned about and refused is gone for good. Say so — a link that
    // disappeared without a word is the worst thing this app could do.
    const rejected = (result.results ?? []).filter((r) => !r.ok);
    if (rejected.length > 0) toast(copy.submit.failed, copy.submit.rejectedOffline(rejected.length));

    if (result.member) applyServerState(result as unknown as ServerState);
    set({ online: true, pending: localQueue.all().length });
    if (localQueue.all().length > 0) {
      flushing = false;
      await flushQueue();
      return;
    }
  } catch (error) {
    // A 4xx is the server's considered answer, not a bad connection. Retrying it every 30 seconds
    // forever would pin the app into a false "offline" state and never clear.
    if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
      if (error.status === 401) {
        localState.forgetSession();
        set({ phase: 'login', server: null });
      } else {
        localQueue.remove(items.map((item) => item.clientId));
        toast(copy.submit.failed, copy.submit.rejectedOffline(items.length));
      }
      set({ pending: localQueue.all().length });
    } else {
      set({ online: false });
    }
  } finally {
    flushing = false;
  }
}

export function watchConnection(): () => void {
  const goOnline = () => {
    set({ online: true });
    void flushQueue();
  };
  const goOffline = () => set({ online: false });
  window.addEventListener('online', goOnline);
  window.addEventListener('offline', goOffline);
  const timer = window.setInterval(() => {
    if (navigator.onLine && localQueue.all().length > 0) void flushQueue();
  }, 30_000);
  return () => {
    window.removeEventListener('online', goOnline);
    window.removeEventListener('offline', goOffline);
    window.clearInterval(timer);
  };
}

/* ------------------------------------------------------------------ posada */

/**
 * The crew's writes are **not** queued offline, and that is deliberate.
 *
 * Everything in `act` is a fact about the student's own work that stays true however late it
 * arrives — a link posted on a train is still that link an hour later. A message to another person
 * is not: „ostavio sam komentar" replayed from a queue three days after the fact lands on somebody
 * who went and looked and found nothing. If it did not go through, say so and let them press it
 * again.
 */
async function posadaCall(run: () => Promise<ServerState & { ok?: boolean }>): Promise<ActResult> {
  if (!state.server) return { ok: false, message: copy.submit.failed };
  try {
    const next = await run();
    if (next.member) applyServerState(next);
    // Anything that changes the crew changes what the crew screen should be showing: a work that
    // has just been commented on is no longer the one waiting longest.
    void loadPosadaView(true);
    return { ok: true };
  } catch (error) {
    if (error instanceof ApiError) {
      const code = String((error.detail as { error?: string })?.error ?? '');
      const known = copy.posada.refusals[code];
      if (known) return { ok: false, message: known };
      if (error.status === 429) return { ok: false, message: copy.login.tooMany };
    }
    return { ok: false, message: state.online ? copy.submit.failed : copy.error.offlineBody };
  }
}

/**
 * Who is where, and whose work is waiting. Pulled rather than pushed: it is about other people, it
 * goes stale on its own, and it must never hold up the screen a person came here for. The map and
 * the crew screen share one copy, refreshed at most every twenty seconds.
 */
const POSADA_TTL_MS = 20_000;
let posadaFetchedAt = 0;
let loadingPosada = false;

export async function loadPosadaView(force = false): Promise<void> {
  if (!state.server || isReadOnly()) return;
  if (loadingPosada) return;
  if (!force && Date.now() - posadaFetchedAt < POSADA_TTL_MS) return;
  loadingPosada = true;
  try {
    const view = await api.posada.view();
    posadaFetchedAt = Date.now();
    set({ posadaView: view });
  } catch {
    // The crew is decoration on a bad connection: the card the student came for still works.
  } finally {
    loadingPosada = false;
  }
}

export const leaveComment = (memberId: string, nodeId: string): Promise<ActResult> =>
  posadaCall(() => api.posada.comment(memberId, nodeId));

export const replyToComment = (id: string, action: 'hvala' | 'nema'): Promise<ActResult> =>
  posadaCall(() => api.posada.reply(id, action));

export const savePosadaMe = (patch: { name?: string; visible?: boolean }): Promise<ActResult> =>
  posadaCall(() => api.posada.me(patch));

/**
 * Marks notifications read. Fire-and-forget: the worst case of a lost acknowledgement is that a
 * nice thing gets said to somebody twice.
 */
export async function ackPosada(ids: string[]): Promise<void> {
  if (ids.length === 0) return;
  try {
    const next = await api.posada.seen(ids);
    if (next.member) applyServerState(next);
  } catch {
    /* it will still be there next time */
  }
}

/* ------------------------------------------------------------------ dvoboj */

/**
 * Every duel write goes through here, and none of them is queued offline.
 *
 * Same rule as the crew, and it bites harder: a challenge, an acceptance or a vote replayed three
 * days late lands in a duel that has already been decided. If it did not go through, say so.
 */
async function duelCall<T extends Partial<ServerState> & Partial<DuelView>>(
  run: () => Promise<T>,
): Promise<ActResult> {
  if (!state.server) return { ok: false, message: copy.submit.failed };
  if (isReadOnly()) return { ok: false, message: copy.login.readOnly };
  try {
    const next = await run();
    if (next.member) applyServerState(next as ServerState);
    // Only into the arena that is still open. A response landing after somebody walked away must
    // not put a duel back on a screen they left.
    if (next.duel && (state.openDuel === null || state.openDuel === next.duel.id))
      set({ duelView: next as DuelView, openDuel: state.openDuel ? next.duel.id : state.openDuel });
    // The duel it just changed may be one somebody else can vote on, and the crew screen is where
    // that is offered.
    void loadPosadaView(true);
    // A write that did not carry a whole state response still moved the row SADA reads, so pull
    // one. Swallowed: the arena already has the fresh duel, and a failed refresh is a stale card
    // rather than a broken screen.
    if (!next.member) void api.state().then(applyServerState).catch(() => undefined);
    return { ok: true };
  } catch (error) {
    if (error instanceof ApiError) {
      const code = String((error.detail as { error?: string })?.error ?? '');
      const known = copy.dvoboj.refusals[code] ?? copy.posada.refusals[code];
      if (known) return { ok: false, message: known };
      if (code === 'bad-link') return { ok: false, message: copy.submit.badUrl };
      if (code === 'duplicate-link') return { ok: false, message: copy.submit.duplicate };
      if (code === 'same-as-first') return { ok: false, message: copy.submit.sameAsFirst };
      if (error.status === 429) return { ok: false, message: copy.login.tooMany };
    }
    return { ok: false, message: state.online ? copy.submit.failed : copy.error.offlineBody };
  }
}

/**
 * Pull a fresh state response now, rather than on the next write or poll.
 *
 * Used by the one thing that has to appear the moment it happens: a challenge landing while the
 * two of them are standing next to each other. Swallowed on failure — the next poll gets it.
 */
export async function refreshStateNow(): Promise<void> {
  if (!state.server) return;
  try {
    applyServerState(await api.state());
  } catch {
    /* the queue flush or the next write will land eventually */
  }
}

/**
 * The path a duel lives at.
 *
 * A real path rather than a fragment, because `app.tsx` routes on the pathname — the drafted Skool
 * post is the thing that brings the voters, and a link that lands them on SADA is a megaphone
 * pointed at the wrong room.
 */
export const duelPath = (id: string): string => `/dvoboj/${encodeURIComponent(id)}`;

/** Open the arena on a duel, fetching it if this is the first look. */
export async function openDuel(id: string): Promise<void> {
  sfx.move();
  set({ openDuel: id, screen: 'dvoboj', duelView: state.duelView?.duel.id === id ? state.duelView : null });
  // So the address bar says where you are, and the link somebody sent you keeps working when they
  // press back. `replaceState` on the way out, in `closeDuel`.
  try {
    if (window.location.pathname !== duelPath(id)) window.history.pushState({}, '', duelPath(id));
  } catch {
    /* nothing here depends on it */
  }
  await loadDuel(id);
}

export async function loadDuel(id: string): Promise<void> {
  try {
    const view = await api.dvoboj.one(id);
    // A slower fetch landing after the member has walked away must not repaint the screen they
    // moved to. Same guard the journal uses.
    if (state.openDuel !== id) return;
    set({ duelView: view });
  } catch {
    /* The arena stays on whatever it had. A duel is never lost by failing to fetch it. */
  }
}

export function closeDuel(): void {
  set({ openDuel: null, duelView: null });
  try {
    if (window.location.pathname.startsWith('/dvoboj/')) window.history.replaceState({}, '', '/');
  } catch {
    /* nothing here depends on it */
  }
}

export const challenge = (body: {
  memberId: string;
  nodeId: string;
  workHours: number;
  voteHours: number;
  islandId?: string;
}): Promise<ActResult> => duelCall(() => api.dvoboj.challenge(body));

export const answerDuel = (
  id: string,
  body: { action: 'prihvati' | 'odbij' | 'izmena'; nodeId?: string; workHours?: number; voteHours?: number },
): Promise<ActResult> => duelCall(() => api.dvoboj.answer(id, body));

export const submitDuel = (id: string, url: string): Promise<ActResult> =>
  duelCall(() => api.dvoboj.submit(id, url));

export const voteDuel = (id: string, label: BlindLabel, axis: Osa): Promise<ActResult> =>
  duelCall(() => api.dvoboj.vote(id, label, axis));

export const reviseDuel = (id: string, url: string): Promise<ActResult> =>
  duelCall(() => api.dvoboj.revise(id, url));

export const forfeitDuel = (id: string): Promise<ActResult> =>
  duelCall(() => api.dvoboj.forfeit(id));

/** Fire-and-forget: the cost of a lost acknowledgement is being told the same thing twice. */
export async function ackDuel(id: string): Promise<void> {
  try {
    await api.dvoboj.seen(id);
  } catch {
    /* it will still be there next time */
  }
}

/* ------------------------------------------------------------------ navigation */

export function go(screen: Screen): void {
  sfx.move();
  set({ screen, openNode: null, focusPeer: null });
}

export function openIsland(islandId: string): void {
  set({ screen: 'ostrvo', openIsland: islandId, openNode: null });
}

export function openNode(nodeId: string | null): void {
  if (nodeId) sfx.confirm();
  set({ openNode: nodeId });
}

/**
 * Go and look at one person's work. Reached from „VRATI KOMENTAR" and from walking into somebody
 * on the map — both of which are the same intention: this person, their posts, now.
 */
export function openPeer(memberId: string | null): void {
  if (memberId) sfx.confirm();
  set({ screen: memberId ? 'posada' : state.screen, focusPeer: memberId, openNode: null });
}

export function setBudget(budget: TimeBudget): void {
  sfx.move();
  // A different amount of time is a different question, so it gets an unskipped answer.
  set({ budget, offerSkip: 0 });
}

/** „DRUGA VEŽBA" — one step down the same ranking. Never random, never capped. */
export function nextOffer(): void {
  sfx.move();
  set({ offerSkip: state.offerSkip + 1 });
}

export function clearFatal(): void {
  set({ fatal: null });
}

/* ------------------------------------------------------------------ progressive disclosure */

function readSeenFeatures(): string[] {
  try {
    return JSON.parse(localStorage.getItem('talas.features.v1') ?? '[]') as string[];
  } catch {
    return [];
  }
}

/* ------------------------------------------------------------------ not seeing somebody */

const HIDDEN_KEY = 'talas.sakriveni.v1';

function readHidden(): string[] {
  try {
    return JSON.parse(localStorage.getItem(HIDDEN_KEY) ?? '[]') as string[];
  } catch {
    return [];
  }
}

/**
 * Stop seeing one person, or start again.
 *
 * This device only, and it goes no further: no request, no row, no list to maintain, and the other
 * member is never told. That is the entire design, and the restraint is the point — a paid, named
 * community of a few hundred people already has a moderator, and building a block list here would
 * mean building a report queue, an appeal and somebody to read them.
 *
 * It exists because the alternative escape was the whole feature: `visible = 0` takes you out of
 * the crew entirely, which also takes away every comment button in both directions. Being bothered
 * by one person should not cost that.
 */
export function toggleHidden(memberId: string): void {
  const next = state.hidden.includes(memberId)
    ? state.hidden.filter((id) => id !== memberId)
    : [...state.hidden, memberId];
  try {
    localStorage.setItem(HIDDEN_KEY, JSON.stringify(next));
  } catch {
    /* private mode */
  }
  set({ hidden: next });
}

export function markFeatureSeen(feature: string): void {
  if (state.seenFeatures.includes(feature)) return;
  const next = [...state.seenFeatures, feature];
  try {
    localStorage.setItem('talas.features.v1', JSON.stringify(next));
  } catch {
    /* private mode */
  }
  set({ seenFeatures: next });
}
